import React, { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import styled from "styled-components";
import CarouselCard from "./CarouselCard";
import CarouselNavigation from "./CarouselNavigation";
import CarouselReadAllCard from "./CarouselReadAllCard";

const SLIDE_SPACING = 25;

const EmblaViewport = styled.div`
  overflow: hidden;
  padding: 20px 4px;
  margin: -20px 4px;

  @media (min-width: 768px) {
    padding: 20px 10px;
    margin: -20px -10px;
  }
`;

const EmblaContainer = styled.div`
  display: flex;
  touch-action: pan-y pinch-zoom;
  margin-left: -10px;

  @media (min-width: 768px) {
    margin-left: -${SLIDE_SPACING}px;
  }
`;

const EmblaSlide = styled.div`
  flex: 0 0 100%;
  min-width: 0;
  padding-left: 10px;
  box-sizing: border-box;
  display: flex;

  @media (min-width: 768px) {
    padding-left: ${SLIDE_SPACING}px;
    flex: 0 0 calc(100% / 3);
  }
`;

const ReviewCarousel = ({ reviews, truncateDest, onReadMore, lines }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    slidesToScroll: 1,
    containScroll: "trimSnaps",
  });

  const [selectedSnap, setSelectedSnap] = useState(0);
  const [prevDisabled, setPrevDisabled] = useState(true);
  const [nextDisabled, setNextDisabled] = useState(true);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedSnap(emblaApi.selectedScrollSnap());
    setPrevDisabled(!emblaApi.canScrollPrev());
    setNextDisabled(!emblaApi.canScrollNext());
  }, [emblaApi]);

  const onReinit = useCallback(() => {
    if (!emblaApi) return;
    onSelect();
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onReinit);

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onReinit);
    };
  }, [emblaApi, onSelect, onReinit]);

  const showReadAll = reviews.length >= 8;

  const items = reviews.map((review) => ({
    type: "review",
    review,
  }));
  if (showReadAll) {
    items.push({ type: "readAll" });
  }

  return (
    <div className="fdn-carousel-cards">
      <EmblaViewport className="fdn-carousel-outer" ref={emblaRef}>
        <EmblaContainer className="fdn-carousel-track">
          {items.map((item) => (
            <EmblaSlide
              key={item.type === "review" ? item.review.id : "read-all"}
              className="fdn-carousel-slide"
            >
              {item.type === "review" ? (
                <CarouselCard
                  review={item.review}
                  onReadMore={onReadMore}
                  lines={lines}
                />
              ) : (
                <CarouselReadAllCard truncateDest={truncateDest} />
              )}
            </EmblaSlide>
          ))}
        </EmblaContainer>
      </EmblaViewport>
      {items.length > 1 && (
        <CarouselNavigation
          selectedSnap={selectedSnap}
          slideCount={items.length}
          prevDisabled={prevDisabled}
          nextDisabled={nextDisabled}
          onPrev={() => emblaApi && emblaApi.scrollPrev()}
          onNext={() => emblaApi && emblaApi.scrollNext()}
          onGoTo={(index) => emblaApi && emblaApi.scrollTo(index)}
          hideOnDesktop={items.length <= 3}
        />
      )}
    </div>
  );
};

export default ReviewCarousel;



// WEBPACK FOOTER //
// ./src/components/carousel/ReviewCarousel.jsx